import { useState, type KeyboardEvent } from 'react';
import type { UsageOverview } from '@/billing/api';
import { balanceCredits, wholeBalance } from '@/billing/plan-math';
import { formatCopy } from '@/i18n/ns';
import { credits, dayTicks, dayTime, isoDay, niceScale, type Formatters } from './format';
import type { UsageCopy } from './copy';

const DAY_MS = 86_400_000;

export interface BalanceChartProps {
  days: string[];
  /** Company credits spent each day (all services). */
  spend: number[];
  overview: UsageOverview;
  now: Date;
  /** Credits the next renewal adds, and when, if it is close enough to show. */
  next: { at: string; credits: number; newPlan: boolean } | null;
  f: Formatters;
  copy: UsageCopy;
}

export interface Grant {
  index: number;
  fraction: number;
  amount: number;
  kind: 'renewed' | 'newPlan' | 'added';
}

function grantKind(reason: string | null, kind: string): Grant['kind'] {
  if (kind !== 'plan_invoice') return 'added';
  return reason === 'subscription_cycle' ? 'renewed' : 'newPlan';
}

function grantsByDay(overview: UsageOverview, days: string[]): Grant[] {
  const indexOf = new Map(days.map((day, i) => [day, i]));
  return overview.grants
    .map((grant) => {
      const at = new Date(grant.at);
      const index = indexOf.get(isoDay(at));
      if (index === undefined) return null;
      return {
        index,
        fraction: (at.getTime() - dayTime(days[index])) / DAY_MS,
        amount: credits(grant.creditMicros),
        kind: grantKind(grant.billingReason, grant.kind),
      };
    })
    .filter((grant): grant is Grant => grant !== null);
}

const KIND_ORDER: Grant['kind'][] = ['added', 'renewed', 'newPlan'];
/** Flags closer than this (% of the width) are one flag; closer than LONG_GAP they drop the date. */
const MERGE_GAP = 8;
const LONG_GAP = 22;
/** Past this point a flag opens to the left, towards any flag before it. */
export const FLIP_AT = 70;

export interface Flag {
  index: number;
  left: number;
  amount: number;
  kind: Grant['kind'];
  long: boolean;
}

/**
 * One flag per day, and one for grants too close to read apart: several grants
 * on the same day (a plan change and its first invoice, an operator grant) add
 * up instead of drawing over each other.
 */
export function grantFlags(grants: Grant[], leftOf: (grant: Grant) => number): Flag[] {
  const flags: Flag[] = [];
  const ordered = [...grants].sort((a, b) => a.index + a.fraction - (b.index + b.fraction));
  for (const grant of ordered) {
    const left = leftOf(grant);
    const last = flags[flags.length - 1];
    const gap = last ? left - last.left : Infinity;
    if (
      last &&
      (last.index === grant.index || gap < MERGE_GAP || (left > FLIP_AT && gap < LONG_GAP))
    ) {
      last.amount += grant.amount;
      last.index = grant.index;
      last.left = left;
      if (KIND_ORDER.indexOf(grant.kind) > KIND_ORDER.indexOf(last.kind)) last.kind = grant.kind;
      continue;
    }
    if (last && gap < LONG_GAP) last.long = false;
    flags.push({ index: grant.index, left, amount: grant.amount, kind: grant.kind, long: true });
  }
  return flags;
}

interface Model {
  points: Array<[number, number]>;
  ends: number[];
  centers: number[];
  grants: Grant[];
  last: number;
  xEnd: number;
  nextAt: number;
}

/** A step line: spending runs down through each day and a paid invoice steps it up. */
function model({ days, spend, overview, now, next }: BalanceChartProps): Model {
  const ends = days.map((day) => {
    const point = overview.balance.find((entry) => entry.day === day);
    return credits(point?.balanceMicros ?? 0);
  });
  const grants = grantsByDay(overview, days);
  const todayIndex = days.indexOf(isoDay(now));
  const nowFraction = (now.getTime() - dayTime(isoDay(now))) / DAY_MS;
  const points: Array<[number, number]> = [];
  const centers: number[] = [];
  days.forEach((_, i) => {
    const added = grants.filter((grant) => grant.index === i);
    const window = i === todayIndex ? Math.max(nowFraction, 0.001) : 1;
    const start = ends[i] + spend[i] - added.reduce((sum, grant) => sum + grant.amount, 0);
    if (i === 0) points.push([0, start]);
    let addedSoFar = 0;
    for (const grant of added.sort((a, b) => a.fraction - b.fraction)) {
      const t = Math.min(grant.fraction, window);
      const before = start + addedSoFar - spend[i] * Math.min(t / window, 1);
      points.push([i + t, before]);
      addedSoFar += grant.amount;
      points.push([i + t, before + grant.amount]);
    }
    const endX = i === todayIndex ? i + window : i + 1;
    points.push([endX, ends[i]]);
    centers.push(endX);
  });
  const last = centers[centers.length - 1] ?? 1;
  let nextAt = -1;
  let xEnd = days.length;
  if (next && todayIndex === days.length - 1) {
    nextAt = todayIndex + (new Date(next.at).getTime() - dayTime(days[todayIndex])) / DAY_MS;
    xEnd = nextAt + 0.62;
  }
  return { points, ends, centers, grants, last, xEnd, nextAt };
}

function Tip({
  props,
  m,
  index,
  x,
}: {
  props: BalanceChartProps;
  m: Model;
  index: number;
  x: number;
}) {
  const { days, spend, now, f, copy } = props;
  const isToday = days[index] === isoDay(now);
  const meta = [
    formatCopy(isToday ? copy.spentSoFar : copy.spent, { credits: f.amount(spend[index]) }),
    ...m.grants
      .filter((grant) => grant.index === index)
      .map((grant) =>
        formatCopy(
          grant.kind === 'renewed'
            ? copy.atRenewal
            : grant.kind === 'newPlan'
              ? copy.fromNewPlan
              : copy.added,
          { credits: f.int(grant.amount) },
        ),
      ),
  ];
  return (
    <div
      className="cu-tip is-balance"
      role="status"
      style={{ left: `${x}%`, transform: `translateX(${x < 12 ? -10 : x > 88 ? -90 : -50}%)` }}
    >
      <div className="cu-tip-date">
        {isToday
          ? formatCopy(copy.today, { time: f.time(now.toISOString()) })
          : f.longDay(days[index])}
      </div>
      <div className="cu-tip-big">
        {formatCopy(copy.balanceValue, { credits: f.int(wholeBalance(m.ends[index])) })}
      </div>
      <div className="cu-tip-lab">{meta.join(' · ')}</div>
    </div>
  );
}

export function BalanceChart(props: BalanceChartProps) {
  const { days, now, next, f, copy } = props;
  const [hover, setHover] = useState(-1);
  const m = model(props);
  const scale = niceScale(Math.max(1, ...m.points.map(([, value]) => value)), 3);
  const X = (t: number) => (t / m.xEnd) * 1000;
  const Y = (value: number) => (1 - Math.max(0, value) / scale.top) * 200;
  const pct = (t: number) => X(t) / 10;
  const line = `M ${m.points.map(([t, v]) => `${X(t).toFixed(2)} ${Y(v).toFixed(2)}`).join(' L ')}`;
  const firstX = X(m.points[0]?.[0] ?? 0).toFixed(2);
  const lastX = X(m.points[m.points.length - 1]?.[0] ?? 0).toFixed(2);
  const area = `${line} L ${lastX} 200 L ${firstX} 200 Z`;
  const todayIndex = days.indexOf(isoDay(now));
  const nowValue = todayIndex >= 0 ? m.ends[todayIndex] : null;
  const balanceNow = balanceCredits(props.overview.account.creditBalanceMicros);
  const move = (direction: number) =>
    setHover((current) => {
      const target = current < 0 ? (direction > 0 ? 0 : m.centers.length - 1) : current + direction;
      return Math.max(0, Math.min(m.centers.length - 1, target));
    });
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
    } else if (event.key === 'Escape') setHover(-1);
  };
  const ticks = dayTicks(days, (i) => pct(i + 0.5), f);
  return (
    <div
      className="cu-chart"
      tabIndex={0}
      role="group"
      aria-label={formatCopy(copy.balanceAria, {
        from: f.day(days[0]),
        to: f.day(days[days.length - 1]),
        balance: f.int(balanceNow),
      })}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setHover(-1)}
      onBlur={() => setHover(-1)}
    >
      <div className="cu-plot is-balance">
        <div className="cu-data">
          {scale.ticks.map((tick) => (
            <div
              key={tick}
              className={`cu-grid${tick === 0 ? ' is-base' : ''}`}
              style={{ bottom: `${(tick / scale.top) * 100}%` }}
            >
              <span className="cu-grid-label">{f.int(tick)}</span>
            </div>
          ))}
          {todayIndex >= 0 && <div className="cu-future" style={{ left: `${pct(m.last)}%` }} />}
          <svg
            className="cu-svg"
            viewBox="0 0 1000 200"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path className="cu-area" d={area} />
            <path className="cu-line" d={line} />
          </svg>
          {m.grants.map((grant) => (
            <span
              key={`line-${grant.index}-${grant.fraction}`}
              className="cu-stepline"
              style={{
                left: `${pct(grant.index + grant.fraction)}%`,
                height: `calc(${Y(m.ends[grant.index]) / 2}% + 10px)`,
              }}
            />
          ))}
          {grantFlags(m.grants, (grant) => pct(grant.index + grant.fraction)).map((flag) => {
            const label =
              flag.kind === 'renewed'
                ? copy.flagRenewed
                : flag.kind === 'newPlan'
                  ? copy.flagNewPlan
                  : copy.flagAdded;
            return (
              <span
                key={`flag-${flag.index}`}
                className={`cu-flag ${flag.left > FLIP_AT ? 'is-step-left' : 'is-step'}`}
                style={{ left: `${flag.left}%` }}
              >
                <strong>+{f.int(flag.amount)}</strong>
                {flag.long && (
                  <span className="cu-flag-long">
                    {' '}
                    · {formatCopy(label, { date: f.day(days[flag.index]) })}
                  </span>
                )}
              </span>
            );
          })}
          {next && m.nextAt > 0 && (
            <>
              <span className="cu-marker" style={{ left: `${pct(m.nextAt)}%` }} />
              <span className="cu-flag is-next" style={{ left: `${pct(m.nextAt)}%` }}>
                <strong>+{f.int(next.credits)}</strong>
                <span className="cu-flag-long">
                  {' · '}
                  {formatCopy(next.newPlan ? copy.nextNewPlan : copy.nextRenewal, {
                    date: f.date(next.at),
                  })}
                </span>
              </span>
            </>
          )}
          {nowValue !== null && (
            <span
              className="cu-now"
              style={{ left: `${pct(m.last)}%`, top: `${Y(nowValue) / 2}%` }}
            />
          )}
          {hover >= 0 && (
            <>
              <span className="cu-cross" style={{ left: `${pct(m.centers[hover])}%` }} />
              <span
                className="cu-cross-dot"
                style={{ left: `${pct(m.centers[hover])}%`, top: `${Y(m.ends[hover]) / 2}%` }}
              />
              <Tip props={props} m={m} index={hover} x={pct(m.centers[hover])} />
            </>
          )}
          <div className="cu-hits">
            {m.centers.map((center, i) => {
              const a = i === 0 ? 0 : (m.centers[i - 1] + center) / 2;
              const b =
                i === m.centers.length - 1
                  ? Math.min(m.xEnd, center + 0.5)
                  : (center + m.centers[i + 1]) / 2;
              return (
                <span
                  key={days[i]}
                  className="cu-hit"
                  style={{ left: `${pct(a)}%`, width: `${pct(b) - pct(a)}%` }}
                  onMouseEnter={() => setHover(i)}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div className="cu-xaxis" aria-hidden="true">
        {ticks.map((tick) => (
          <span
            key={`${tick.label}-${tick.pos}`}
            className={[tick.edge ? `is-${tick.edge}` : '', tick.minor ? 'is-minor' : '']
              .filter(Boolean)
              .join(' ')}
            style={{ left: `${tick.pos.toFixed(2)}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>
    </div>
  );
}
